import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import TenantFormNameFields from './components/TenantFormNameFields.js.jsx';
import TenantFormAddressFields from './components/TenantFormAddressFields.js.jsx';
import LeaseCustomFieldGroup from './components/LeaseCustomFieldGroup.js.jsx';
import TenantFormContactSection from './components/TenantFormContactSection.js.jsx';
import { useDebounce } from './components/Hooks';
import classNames from 'classnames';
import tenantFormStyles from '../../stylesheets/management_site/tenantForm.module.scss';
import newLeaseModalStyles from '../../stylesheets/management_site/newLeaseModal.module.scss';

const NewTenantAdded = ({
  primaryTenantRole,
  tenantIndex,
  tenantDisplayNumber,
  tenantRoles,
  country,
  state,
  unit,
  lease,
  licenseData,
  onDelete,
  tenantTermOverride,
  leaseProfile,
  newTenantCustomFields,
  customFieldTypeGroups,
  addressCountryList = [],
  addressStateList = {},
  parsedTenantData = null,
}) => {
  const [usePropertyAddress, setUsePropertyAddress] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(country);
  const [selectedState, setSelectedState] = useState(state);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [stateLabel, setStateLabel] = useState('State');
  const [zipLabel, setZipLabel] = useState('Zip Code');
  const [showClearButton, setShowClearButton] = useState(false);
  const [allowOnlineAccess, setAllowOnlineAccess] = useState(
    lease.tenant_lease_relationships?.some((r) => r.allow_online_access) ||
      leaseProfile?.allow_online_access
  );
  const [showOnlineAccessColumn, setShowOnlineAccessColumn] = useState(true);

  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isPopulatingFromParsedData, setIsPopulatingFromParsedData] =
    useState(false);

  const searchXhrRef = useRef(null);
  const stateUpdateQueue = useRef(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 750);

  const handleCountryDOMChange = (e) => {
    const newCountry = e.target.value;
    setSelectedCountry(newCountry);

    const firstState = getFirstStateForCountry(newCountry);
    setSelectedState(firstState);

    if (newCountry === 'Canada') {
      setStateLabel('Province');
      setZipLabel('Postal Code');
    } else {
      setStateLabel('State');
      setZipLabel('Zip Code');
    }
  };

  const handleStateDOMChange = (e) => {
    setSelectedState(e.target.value);
  };

  const updateSelect2State = (stateValue) => {
    const container = document.getElementById(`new-tenant-${tenantIndex}`);
    if (!container) return false;

    const stateSelect = container.querySelector(`select[name*="[state]"]`);

    if (stateSelect && $(stateSelect).data('select2')) {
      $(stateSelect).val(stateValue).trigger('change.select2');
      $(stateSelect).trigger('change');
      return true;
    }
    return false;
  };

  useLayoutEffect(() => {
    const container = document.getElementById(`new-tenant-${tenantIndex}`);
    window.mdc.autoInit(container);
    window.attachFormatMoneyListener();
    window.initDatepicker(`#new-tenant-${tenantIndex}`);
    window.initDateTimepicker(`#new-tenant-${tenantIndex}`);

    window.initSelect2(`#new-tenant-${tenantIndex}`);

    const countrySelect = $(container).find(`select[name*="[country]"]`);
    const stateSelect = $(container).find(`select[name*="[state]"]`);

    countrySelect.on('change select2:select', handleCountryDOMChange);
    stateSelect.on('change select2:select', handleStateDOMChange);

    if (stateUpdateQueue.current && stateSelect.data('select2')) {
      updateSelect2State(stateUpdateQueue.current);
      stateUpdateQueue.current = null;
    }

    return () => {
      const container = document.getElementById(`new-tenant-${tenantIndex}`);
      if (container) {
        const countrySelect = $(container).find(`select[name*="[country]"]`);
        const stateSelect = $(container).find(`select[name*="[state]"]`);
        countrySelect.off('change select2:select', handleCountryDOMChange);
        stateSelect.off('change select2:select', handleStateDOMChange);
      }
    };
  }, [tenantIndex]);

  useEffect(() => {
    return () => {
      if (searchXhrRef.current && searchXhrRef.current.readyState !== 4) {
        searchXhrRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const customerDefaults = { country, state };
    if (!customerDefaults.country && !customerDefaults.state) return;

    const needsDefaults = !country && !state;

    if (needsDefaults && customerDefaults.country) {
      setSelectedCountry(customerDefaults.country);

      if (customerDefaults.country === 'Canada') {
        setStateLabel('Province');
        setZipLabel('Postal Code');
      } else {
        setStateLabel('State');
        setZipLabel('Zip Code');
      }

      if (customerDefaults.state) {
        setSelectedState(customerDefaults.state);
      } else {
        const firstState = getFirstStateForCountry(customerDefaults.country);
        setSelectedState(firstState);
      }
    }
  }, [country, state]);

  useLayoutEffect(() => {
    if (!parsedTenantData || tenantIndex !== 0) return;

    setIsPopulatingFromParsedData(true);
    populateFromSelectedTenant(parsedTenantData);
    setIsPopulatingFromParsedData(false);
  }, [parsedTenantData, tenantIndex]);

  useEffect(() => {
    if (searchXhrRef.current && searchXhrRef.current.readyState !== 4) {
      searchXhrRef.current.abort();
    }

    if (!debouncedSearchTerm || debouncedSearchTerm.length < 1) {
      setSearchResults([]);
      setShowAutocomplete(false);
      return;
    }

    setIsSearching(true);

    searchXhrRef.current = $.ajax({
      url: '/entities/role_search_redesign',
      type: 'post',
      dataType: 'json',
      data: {
        q: debouncedSearchTerm,
        role: 'tenants',
        limit: 20,
      },
      success: (data) => {
        setSearchResults(data || []);
        setShowAutocomplete(data && data.length > 0);
        setIsSearching(false);
      },
      error: () => {
        setSearchResults([]);
        setShowAutocomplete(false);
        setIsSearching(false);
      },
    });
  }, [debouncedSearchTerm]);

  const handleUsePropertyAddress = (e) => {
    setUsePropertyAddress(e.target.checked);
  };

  const getFirstStateForCountry = (country) => {
    if (!country || !addressStateList) {
      return '';
    }

    const countryStates = addressStateList[country];

    if (!countryStates) {
      return '';
    }

    const stateNames = Object.values(countryStates);
    if (stateNames.length > 0) {
      return countryStates[stateNames[0]];
    }
    return '';
  };

  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setSelectedCountry(newCountry);

    if (!isPopulatingFromParsedData) {
      const firstState = getFirstStateForCountry(newCountry);
      setSelectedState(firstState);
    }

    if (newCountry === 'Canada') {
      setStateLabel('Province');
      setZipLabel('Postal Code');
    } else {
      setStateLabel('State');
      setZipLabel('Zip Code');
    }
  };

  const toggleGroupExpansion = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete(tenantIndex);
    }
  };

  const searchEntities = (searchTermValue) => {
    setSearchTerm(searchTermValue);
  };

  const hideAutocomplete = () => {
    setShowAutocomplete(false);
    setSearchResults([]);
    setSearchTerm('');
  };

  const populateFromSelectedTenant = (tenantData) => {
    if (!tenantData) return;

    hideAutocomplete();

    const container = document.getElementById(`new-tenant-${tenantIndex}`);
    if (!container) return;

    const existingCustomFieldIdInputs =
      container.querySelectorAll('.custom_field_id');
    existingCustomFieldIdInputs.forEach((input) => input.remove());

    const firstNameInput = container.querySelector(
      `input[name*="[firstname]"]`
    );
    if (firstNameInput && tenantData.firstname) {
      firstNameInput.value = tenantData.firstname;
      firstNameInput.dispatchEvent(new Event('change'));
    }

    const lastNameInput = container.querySelector(`input[name*="[lastname]"]`);
    if (lastNameInput && tenantData.lastname) {
      lastNameInput.value = tenantData.lastname;
      lastNameInput.dispatchEvent(new Event('change'));
    }

    const middleNameInput = container.querySelector(`input[name*="[mi]"]`);
    if (middleNameInput && tenantData.mi) {
      middleNameInput.value = tenantData.mi;
      middleNameInput.dispatchEvent(new Event('change'));
    }

    const emailInput = container.querySelector(`input[name*="[email]"]`);
    if (emailInput && tenantData.email) {
      emailInput.value = tenantData.email;
      emailInput.dispatchEvent(new Event('change'));
    }

    const phoneInput = container.querySelector(`input[name*="[phone]"]`);
    if (phoneInput && tenantData.phone) {
      phoneInput.value = tenantData.phone;
      phoneInput.dispatchEvent(new Event('change'));
    }

    ensureHidden(
      `lease[tenant_lease_relationships_attributes][${tenantIndex}]` +
        `[tenant_attributes][address_attributes][id]`,
      tenantData.addressid || '',
      container
    );

    if (tenantData.addressline1) {
      const addressInput = container.querySelector(
        `input[name*="[addressline1]"]`
      );
      if (addressInput) {
        addressInput.value = tenantData.addressline1;
        addressInput.dispatchEvent(new Event('change'));
      }
    }

    const address2 = container.querySelector(`input[name*="[addressline2]"]`);
    if (address2) {
      address2.value = tenantData.addressline2 || '';
      address2.dispatchEvent(new Event('change'));
    }

    const cityIn = container.querySelector(`input[name*="[city]"]`);
    if (cityIn) {
      cityIn.value = tenantData.city || '';
      cityIn.dispatchEvent(new Event('change'));
    }

    const countrySel = container.querySelector(`select[name*="[country]"]`);
    if (countrySel && tenantData.country) {
      setSelectedCountry(tenantData.country);
      if (tenantData.country === 'Canada') {
        setStateLabel('Province');
        setZipLabel('Postal Code');
      } else {
        setStateLabel('State');
        setZipLabel('Zip Code');
      }
      countrySel.value = tenantData.country;
      countrySel.dispatchEvent(new Event('change'));
    }

    if (tenantData.state) {
      setSelectedState(tenantData.state);
      let stateSel = container.querySelector(`select[name*="[state]"]`);
      if (stateSel) {
        stateSel = $(stateSel);
        if (stateSel.hasClass('pm-default-select2-menu')) {
          stateSel.val(tenantData.state).trigger('change.select2');
        }
      }
    }

    const zipIn = container.querySelector(`input[name*="[zipcode]"]`);
    if (zipIn) {
      zipIn.value = tenantData.zipcode || '';
      zipIn.dispatchEvent(new Event('change'));
    }

    const tenantIdInputs = container.querySelectorAll(
      `.tenant_id_${tenantIndex}`
    );
    tenantIdInputs.forEach((input) => {
      input.value = tenantData.id || '';
      input.disabled = false;
    });

    if (tenantData.tenant_role_relationship_id) {
      ensureHidden(
        `lease[tenant_lease_relationships_attributes][${tenantIndex}]` +
          `[tenant_attributes][tenant_role_relationship_attributes][id]`,
        tenantData.tenant_role_relationship_id,
        container
      );
    }

    if (tenantData.custom_fields) {
      Object.entries(tenantData.custom_fields).forEach(
        ([customFieldId, cf]) => {
          const typeIdInput = container.querySelector(
            `input[name*="[custom_field_type_id]"][value="${cf.type_id}"]`
          );
          if (!typeIdInput) return;

          const idx = typeIdInput.name.match(
            /\[(\d+)\]\[custom_field_type_id\]/
          )[1];
          const base =
            `lease[tenant_lease_relationships_attributes][${tenantIndex}]` +
            `[tenant_attributes][tenant_role_relationship_attributes]` +
            `[custom_fields_attributes]`;

          const valueInput = container.querySelector(
            `input[name="${base}[${idx}][value]"]`
          );
          const valueSelect = container.querySelector(
            `select[name="${base}[${idx}][value]"]`
          );

          if (valueInput) {
            if (cf.type === 'checkbox') {
              valueInput.checked = cf.value.toLowerCase() === 'true';
              valueInput.value = valueInput.checked ? 'true' : 'false';
            } else {
              valueInput.value = cf.value;
            }
            valueInput.dispatchEvent(new Event('change'));
          } else if (valueSelect) {
            valueSelect.value = cf.value;
            valueSelect.dispatchEvent(new Event('change'));
          }

          if (tenantData.tenant_role_relationship_id) {
            ensureHidden(
              `${base}[${idx}][id]`,
              customFieldId,
              typeIdInput.parentElement
            );
          }
        }
      );
    }

    setShowClearButton(true);

    const labels = container.querySelectorAll('.mdc-floating-label');
    labels.forEach((label) => {
      const forAttr = label.getAttribute('for');
      const input = container.querySelector(`#${CSS.escape(forAttr)}`);
      if (input && input.value) {
        label.classList.add('mdc-floating-label--float-above');
      }
    });
  };

  const clearEntitySearch = () => {
    const container = document.getElementById(`new-tenant-${tenantIndex}`);
    if (container) {
      const tenantIdInputs = container.querySelectorAll(
        `.tenant_id_${tenantIndex}`
      );
      tenantIdInputs.forEach((input) => {
        input.value = '';
        input.disabled = true;
      });

      const customFieldIdInputs =
        container.querySelectorAll('.custom_field_id');
      customFieldIdInputs.forEach((input) => input.remove());
    }

    const inputs = document.querySelectorAll(
      `#new-tenant-${tenantIndex} input[type="text"], #new-tenant-${tenantIndex} input[type="email"], #new-tenant-${tenantIndex} input[type="tel"], #new-tenant-${tenantIndex} input[type="checkbox"]`
    );
    inputs.forEach((input) => {
      if (!input.name.endsWith('[custom_field_type_id]')) {
        if (input.type === 'checkbox') {
          input.checked = false;
        } else {
          input.value = '';
        }
      }
    });

    const selects = document.querySelectorAll(
      `#new-tenant-${tenantIndex} select`
    );
    selects.forEach((select) => {
      if (select.options.length > 0) {
        select.selectedIndex = 0;
      }
    });

    setSearchResults([]);
    setShowAutocomplete(false);
    setSearchTerm('');
    setShowClearButton(false);
    setShowOnlineAccessColumn(true);
    setAllowOnlineAccess(
      lease.tenant_lease_relationships?.some((r) => r.allow_online_access) ||
        leaseProfile?.allow_online_access
    );

    if (container) {
      const labels = container.querySelectorAll('.mdc-floating-label');
      labels.forEach((label) => {
        label.classList.remove('mdc-floating-label--float-above');
      });
    }
  };

  const ensureHidden = (name, value, container = document.body) => {
    let input = container.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      container.appendChild(input);
    }
    input.value = value;
  };

  let fieldIndex = 0;
  const customFieldGroups = [];

  if (newTenantCustomFields && newTenantCustomFields.length > 0) {
    customFieldGroups.push(
      <LeaseCustomFieldGroup
        key={`tenant-${tenantIndex}-custom-fields`}
        groupName={tenantTermOverride}
        groupId={`tenant-${tenantIndex}-custom-fields`}
        fields={newTenantCustomFields}
        basePath={`lease[tenant_lease_relationships_attributes][${tenantIndex}][tenant_attributes][tenant_role_relationship_attributes][custom_fields_attributes]`}
        startIndex={fieldIndex}
        isExpanded={
          expandedGroups[`tenant-${tenantIndex}-custom-fields`] || false
        }
        onToggle={() =>
          toggleGroupExpansion(`tenant-${tenantIndex}-custom-fields`)
        }
      />
    );
    fieldIndex += newTenantCustomFields.length;
  }

  customFieldTypeGroups?.forEach((group, groupIndex) => {
    if (!group.customFields || group.customFields.length === 0) return;

    const groupId = `tenant-${tenantIndex}-group-${groupIndex}`;
    customFieldGroups.push(
      <LeaseCustomFieldGroup
        key={groupId}
        groupName={group.name}
        groupId={groupId}
        fields={group.customFields}
        basePath={`lease[tenant_lease_relationships_attributes][${tenantIndex}][tenant_attributes][tenant_role_relationship_attributes][custom_fields_attributes]`}
        startIndex={fieldIndex}
        isExpanded={expandedGroups[groupId] || false}
        onToggle={() => toggleGroupExpansion(groupId)}
      />
    );
    fieldIndex += group.customFields.length;
  });

  return (
    <div
      id={`new-tenant-${tenantIndex}`}
      className={`new-tenant-redesign-panel new-tenant-redesign_${tenantIndex}`}
    >
      <h2
        id={`new-tenant-count-${tenantIndex}`}
        className={tenantFormStyles.tenantCount}
      >
        {tenantTermOverride}{' '}
        <span className="new-tenant-count">{tenantDisplayNumber}</span>
      </h2>
      {onDelete && (
        <button
          type="button"
          id="delete-tenant"
          className={classNames(
            'mdc-button',
            'pm-text-color--red1',
            tenantFormStyles.deleteButton
          )}
          onClick={handleDeleteClick}
        >
          Delete {tenantTermOverride}
        </button>
      )}

      <input
        type="hidden"
        name={`lease[tenant_lease_relationships_attributes][${tenantIndex}][tenant_attributes][id]`}
        className={`tenant_id_${tenantIndex}`}
        disabled
      />
      {tenantIndex === 0 && (
        <input
          type="hidden"
          name="lease[unit_lease_relationships_attributes][0][unit_id]"
          value={unit?.id || ''}
        />
      )}
      <input
        type="hidden"
        name={`lease[tenant_lease_relationships_attributes][${tenantIndex}][tenant_attributes][company_name]`}
      />
      <input
        type="hidden"
        name={`lease[tenant_lease_relationships_attributes][${tenantIndex}][tenant_attributes][tenant_role_relationship_attributes][id]`}
      />

      <TenantFormNameFields
        tenantIndex={tenantIndex}
        tenantDisplayNumber={tenantDisplayNumber}
        tenantTermOverride={tenantTermOverride}
        tenantRoles={tenantRoles}
        primaryTenantRole={primaryTenantRole}
        unit={unit}
        searchResults={searchResults}
        isSearching={isSearching}
        showAutocomplete={showAutocomplete}
        onSearchEntities={searchEntities}
        onSelectTenant={populateFromSelectedTenant}
        onHideAutocomplete={hideAutocomplete}
        showClearButton={showClearButton}
        onClearEntitySearch={clearEntitySearch}
        licenseData={licenseData}
      />

      <TenantFormContactSection
        tenantIndex={tenantIndex}
        allowOnlineAccess={allowOnlineAccess}
        onAllowOnlineAccessChange={(e) =>
          setAllowOnlineAccess(e.target.checked)
        }
        showOnlineAccessColumn={showOnlineAccessColumn}
      />

      <TenantFormAddressFields
        tenantIndex={tenantIndex}
        usePropertyAddress={usePropertyAddress}
        onUsePropertyAddressChange={handleUsePropertyAddress}
        selectedCountry={selectedCountry}
        selectedState={selectedState}
        onCountryChange={handleCountryChange}
        onStateChange={(e) => setSelectedState(e.target.value)}
        stateLabel={stateLabel}
        zipLabel={zipLabel}
        addressCountryList={addressCountryList}
        addressStateList={addressStateList}
      />

      {customFieldGroups.length > 0 && (
        <div className={newLeaseModalStyles.newLeaseModalSection}>
          <div className={newLeaseModalStyles.sectionHeader}>
            <h3 className={newLeaseModalStyles.sectionTitle}>Custom Fields</h3>
          </div>
          <div className={newLeaseModalStyles.sectionContent}>
            {customFieldGroups}
          </div>
        </div>
      )}

      <hr />
    </div>
  );
};

export default NewTenantAdded;
